import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

type Props = {
    open: boolean;
    /** Dialog title, already translated. */
    title: string;
    /** Field label, already translated. */
    label: string;
    /** Confirm button text, already translated. */
    confirmText: string;
    /** The current name, shown as the editable starting value. */
    initialValue: string;
    /**
     * Message id saying why a trimmed name cannot be used, or undefined when
     * it can. Shown under the field; the confirm stays blocked meanwhile.
     */
    validate: (trimmed: string) => string | undefined;
    /**
     * Whether the starting value may be confirmed as it is: true when it is a
     * suggestion for something new rather than the name of something existing.
     */
    confirmUnchanged?: boolean;
    onCancel: () => void;
    /** Called with the trimmed name; only ever one `validate` accepts. */
    onConfirm: (value: string) => void;
    inputTestId?: string;
    confirmTestId?: string;
};

/**
 * Asks for a name. Invalid input blocks the confirm and says why — it is never
 * silently corrected, only trimmed — and confirming the starting value
 * unchanged is not offered, since it would change nothing, unless that value
 * is only a suggestion.
 */
export default function NameDialog({
    open,
    title,
    label,
    confirmText,
    initialValue,
    validate,
    confirmUnchanged = false,
    onCancel,
    onConfirm,
    inputTestId,
    confirmTestId,
}: Props) {
    const intl = useIntl();
    const [value, setValue] = useState(initialValue);

    // Each opening edits the CURRENT name, not a previous session's text.
    useEffect(() => {
        if (open) {
            setValue(initialValue);
        }
    }, [open, initialValue]);

    const trimmed = value.trim();
    const errorId = validate(trimmed);
    const canConfirm =
        errorId === undefined && (confirmUnchanged || trimmed !== initialValue);

    const confirm = (): void => {
        if (canConfirm) {
            onConfirm(trimmed);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onCancel}
            maxWidth="xs"
            fullWidth
            PaperProps={{ elevation: 0 }}
        >
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    fullWidth
                    margin="dense"
                    size="small"
                    value={value}
                    onChange={(event) => setValue(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' && canConfirm) {
                            event.preventDefault();
                            confirm();
                        }
                    }}
                    label={label}
                    error={errorId !== undefined}
                    helperText={
                        errorId !== undefined
                            ? intl.formatMessage({ id: errorId })
                            : ' '
                    }
                    inputProps={{ 'data-testid': inputTestId }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} color="inherit">
                    {intl.formatMessage({ id: 'cancel' })}
                </Button>
                <Button
                    onClick={confirm}
                    variant="contained"
                    disabled={!canConfirm}
                    data-testid={confirmTestId}
                >
                    {confirmText}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
